import { useMemo, type FC } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { WarehouseDto } from '@/api';
import { Alert, Badge, Select, Table, type TableColumn } from '@/ui';

import { resolveApiErrorMessage } from '../../lib/apiError';
import { useWarehousesList } from './api/queries';
import { resolveActivity, type ActivityFilter } from './lib/searchParams';

const ROUTE_ID = '/_app/warehouses' as const;
const ROUTE_PATH = '/warehouses' as const;

/*
 * Склады - справочник складов 1С, только чтение: его наполняет поток из 1С
 * (architecture/contracts/onec-warehouses.md). Из действующих менеджер
 * выбирает склад отправки в окне отгрузки.
 */
export const WarehousesPage: FC = () => {
  const navigate = useNavigate();
  const activity = resolveActivity(useSearch({ from: ROUTE_ID }));

  const { data, isPending, isError, error } = useWarehousesList(activity);
  const rows = data ?? [];

  const columns = useMemo<ReadonlyArray<TableColumn<WarehouseDto>>>(
    () => [
      {
        id: 'name',
        header: 'Наименование',
        cell: (warehouse) => (
          <span className="font-[550] text-ink-900">{warehouse.name}</span>
        ),
      },
      {
        id: 'isActive',
        header: 'Состояние',
        width: 'w-44',
        cell: (warehouse) =>
          warehouse.isActive ? (
            <Badge tone="completed" withDot>
              Действует
            </Badge>
          ) : (
            <Badge tone="neutral" withDot>
              Удалён в 1С
            </Badge>
          ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="mb-3.5 flex flex-wrap items-center gap-2 rounded-lg border border-ink-200 bg-white px-3.5 py-3">
        <Select
          aria-label="Действие склада"
          value={activity}
          onChange={(event) => {
            const next = event.target.value as ActivityFilter;
            void navigate({
              from: ROUTE_PATH,
              to: ROUTE_PATH,
              search: next === 'active' ? {} : { activity: next },
            });
          }}
        >
          <option value="active">Действующие</option>
          <option value="inactive">Удалённые в 1С</option>
          <option value="all">Все</option>
        </Select>
      </div>

      {isError ? (
        <Alert tone="error">
          Не удалось загрузить склады: {resolveApiErrorMessage(error)}.
        </Alert>
      ) : (
        <Table
          columns={columns}
          rows={rows}
          getRowKey={(warehouse) => warehouse.id}
          isLoading={isPending}
          emptyLabel={
            activity === 'inactive'
              ? 'Удалённых складов нет'
              : 'Складов пока нет: справочник наполняется из 1С'
          }
          footer={rows.length === 0 ? null : `Складов: ${rows.length}`}
        />
      )}
    </>
  );
};
